import { useCallback, useMemo } from "react";
import {
    Box,
    Checkbox,
    Divider,
    List,
} from "@mui/material";
import { QuestionInterfaceProps } from "../types";
import { ICandidate } from "../../domain/candidate";
import { Loading } from "../../components/loading";
import { CandidateRow } from "../../components/candidate-row";
import { PreferenceVote } from "../preference/preference-typedef";
import { CANDIDATE_DISPLAY_FORMAT } from "../../domain/ballot-api";
import { firstPastValidate } from "./check-list-validate";
import { IQuestionState } from "../../domain/ballot-state";
import { shuffleCandidates } from "../../util/shuffle_candidates";
import { CHECK_LIST } from "./check-list-typedef";
import { sortDisplayOrder } from "../../pages/election/util";

export const FirstPastQuestion = ({ question, setQuestionState, questionState }: QuestionInterfaceProps<PreferenceVote>) => {
    const { valid } = questionState;
    const value = CHECK_LIST.normalize(questionState)
    const candidates = useMemo(() => {
        if (question && question.candidates) {
            if (question.shuffle_candidates) {
                return shuffleCandidates(question.candidates)
            }
            return question.candidates.sort(sortDisplayOrder);
        }
    }, [question]);

    const max_select = question.voting_config && question.voting_config.max_select ? Number(question.voting_config.max_select) : null;
    const setQuestionVote = useCallback(
        (value: React.SetStateAction<any>) => {
            setQuestionState((response: IQuestionState) => {
                const _value = typeof value === "function" ? value(response.value) : value;

                const validation = firstPastValidate(question, _value);

                return {
                    question_id: question.id,
                    response_type: CHECK_LIST.TYPE,
                    data_version: CHECK_LIST.DATA_VERSION,
                    valid: validation.valid,
                    invalid_reasons: validation.reasons,
                    dirty: true,
                    value: _value
                };
            });
        },
        [setQuestionState, question]
    );

    const handleChange = useCallback(
        (candidate: ICandidate) => {
            setQuestionVote((bv: PreferenceVote) => {

                const alreadyTicked = bv && Object.keys(bv).includes(candidate.id)

                if (max_select === 1) {
                    if (alreadyTicked) {
                        // remove all checks
                        return {}
                    }
                    return {
                        [candidate.id]: {
                            id: candidate.id,
                            label: candidate.label,
                            index: 0
                        }
                    }
                }

                if (alreadyTicked) {
                    const newVote: PreferenceVote = { ...bv }
                    delete newVote[candidate.id]
                    return newVote;
                } else {
                    const newVote = {
                        ...bv,
                        [candidate.id]: {
                            id: candidate.id,
                            label: candidate.label,
                            index: 0
                        }
                    }
                    return newVote;
                }
            });
        },
        [setQuestionVote, max_select]
    );
    if (!candidates) {
        return <Loading source="ResponseFirstPast" />;
    }
    return (
        <Box pt={2}>
            <List disablePadding>
                <Divider component="li" />

                {candidates.map((c, i) => (
                    <CandidateFirstPastRow
                        key={c.id}
                        checked={!!value && c.id in value}
                        candidate={c}
                        error={!valid}
                        onChange={handleChange}
                    />
                ))}
            </List>
        </Box>
    );
};



interface CandidateFirstPastProps {
    checked: boolean;
    error: boolean;
    onChange: (c: ICandidate, checked: boolean) => void;
    candidate: ICandidate;
    display_format?: CANDIDATE_DISPLAY_FORMAT;
}
export const CandidateFirstPastRow = (props: CandidateFirstPastProps) => {
    const { checked, onChange, candidate, error, display_format } = props;
    const handleClick = useCallback(
        (e: any) => {
            onChange(candidate, !checked);
        },
        [candidate, onChange, checked]
    );
    const icon = useMemo(() => {
        return <Checkbox
            checked={checked}
            sx={{ padding: "5px" }}
            color={error ? "error" as any : undefined}
        />
    }, [checked, error])

    return <CandidateRow
        display_format={display_format}
        candidate={candidate}
        icon={icon}
        onClick={handleClick} />
}
